<template>
    <div class="competition-statement ml-md-30">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>
        <template v-else>
            <!-- Intro + PDF downloads + order -->
            <panel-competition-form-box>
                <div class="row w100">
                    <div class="col-12 col-md-8">
                        <h5 class="mab16">Zgody uczestników</h5>
                        <p>
                            Formularz zgód możesz pobrać w zbiorczym pliku PDF, przygotowanym do wydruku. Indywidualne
                            formularze zgód będą wysyłane do uczestników w mailu potwierdzającym rejestrację w zawodach.
                        </p>
                    </div>
                    <div class="col-12 col-md-4">
                        <base-button
                            btn2
                            icon="fas fa-download"
                            :disabled="loadingPdf"
                            :loading="loadingPdfBulk"
                            @click="downloadBulkPdf"
                        >
                            Pobierz zbiorczy plik
                        </base-button>
                        <div class="mat16">
                            <base-button
                                btn2
                                icon="fas fa-download"
                                :disabled="loadingPdf"
                                :loading="loadingPdfEmpty"
                                @click="downloadEmptyPdf"
                            >
                                Pobierz pustą kartę
                            </base-button>
                        </div>
                    </div>
                </div>
                <div class="row w100 mat16">
                    <div class="col-12">
                        <p class="mab16">Kolejność uczestników w pliku PDF</p>
                        <div class="radio-wrap-column">
                            <panel-input-base-radio
                                v-for="option in orderOptions"
                                :key="option.value"
                                v-model="order"
                                name="statementOrder"
                                :option="option"
                            />
                        </div>
                    </div>
                </div>
            </panel-competition-form-box>

            <!-- Print format (local only, for PDF downloads) -->
            <panel-competition-form-box class="mat16">
                <div class="row w100">
                    <div class="col-12 col-md-8">
                        <p class="mab16">Format wydruku</p>
                        <div class="radios2">
                            <input id="perpage_1" v-model="perpage" type="radio" :value="1" />
                            <label for="perpage_1">
                                1 formularz na stronie A4
                                <span class="optional">(zawiera zamówienia ze sklepu)</span>
                            </label>
                            <br />
                            <input id="perpage_2" v-model="perpage" type="radio" :value="2" />
                            <label for="perpage_2">2 formularze na stronie A4</label>
                        </div>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-hint-wrap">
                            <p class="form-hint">
                                Formularz zawierający zamówienia ze sklepu zajmuje całą stronę A4.
                                <br /><br />
                                Formularze bez wykazu zakupionych produktów będą umieszczane po dwa na stronie A4.
                            </p>
                        </div>
                    </div>
                </div>
            </panel-competition-form-box>

            <!-- Language switcher -->
            <div class="lang-switcher mat16 mab16">
                <button
                    v-for="lang in langs"
                    :key="lang"
                    type="button"
                    class="lang-switcher__btn"
                    :class="{ 'lang-switcher__btn--active': activeLang === lang }"
                    @click="activeLang = lang"
                >
                    {{ lang.toUpperCase() }}
                </button>
            </div>

            <!-- Extra fields (per language) -->
            <panel-competition-form-box>
                <div class="row w100">
                    <div class="col-12 col-md-8">
                        <div class="row mab24 statement-preview">
                            <div class="col-3">
                                <p class="txt--s1">Adam</p>
                                <p class="txt--s2">Kowalski</p>
                            </div>
                            <div class="col-3">
                                <p class="txt--s1">Data urodzenia</p>
                                <p class="txt--s2">10.10.1990</p>
                            </div>
                            <div class="col-3">
                                <p class="txt--s1">Miasto</p>
                                <p class="txt--s2">Warszawa</p>
                            </div>
                            <div class="col-3">
                                <p class="txt--s1">Płeć<span class="pull-right">Nr</span></p>
                                <p class="txt--s2">M<span class="pull-right">123</span></p>
                            </div>
                        </div>
                        <p class="mab16">Dodatkowe informacje</p>
                        <div class="row">
                            <div
                                v-for="(field, index) in activeStatement.fields"
                                :key="`${activeLang}-field-${index}`"
                                class="col-6 col-md-3 mab8"
                            >
                                <base-input-select
                                    :value="field"
                                    :options="fieldOptions"
                                    label-select="label"
                                    :reduce="(o) => o.value"
                                    is-panel
                                    :clearable="false"
                                    :searchable="false"
                                    placeholder="Brak"
                                    class="statement-field-select"
                                    @input="setField(index, $event)"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-hint-wrap">
                            <p class="form-hint">Wybierz dodatkowe informacje widoczne na wydruku zgody.</p>
                        </div>
                    </div>
                </div>
            </panel-competition-form-box>

            <!-- Consent contents (per language) -->
            <panel-competition-form-box
                v-for="(content, index) in activeStatement.contents"
                :key="`${activeLang}-content-${index}`"
                class="mat16"
            >
                <div class="row w100">
                    <div class="col-12 col-md-8">
                        <panel-input-area
                            :value="content"
                            label="Treść z miejscem na podpis"
                            :rows="contentRows(content)"
                            @input="setContent(index, $event)"
                        />
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-hint-wrap">
                            <p class="form-hint mat24">
                                Pod treścią zgody znajduje się miejsce na podpis uczestnika.
                                <br /><br />
                                Upewnij się, czy wpisana treść mieści się na wydruku.
                            </p>
                            <base-button btn2 icon="fas fa-trash" class="mat16" @click="removeContent(index)">
                                Usuń
                            </base-button>
                        </div>
                    </div>
                </div>
            </panel-competition-form-box>

            <div class="row mat16 mab24">
                <div class="col-12">
                    <div class="add-new-content-wrap">
                        <base-button btn2 @click="addContent">Dodaj treść zgody</base-button>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="row mat16 mab64 align-items-center">
                <div class="col-12 col-md-5">
                    <base-button
                        btn2
                        icon="fas fa-trash"
                        :disabled="loadingDefault"
                        :loading="loadingDefault"
                        @click="restoreDefault"
                    >
                        Przywróć domyślną treść zgód
                    </base-button>
                </div>
                <div class="col-12 col-md-4 offset-md-3">
                    <base-button btn1 :disabled="saving" :loading="saving" @click="save">Zapisz</base-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import axios from 'axios';
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputBaseRadio from '@/components/PanelInputBaseRadio';
    import PanelInputArea from '@/components/PanelInputArea';

    const FIELD_SLOTS = 8;

    function padFields(fields) {
        const padded = [...(fields || [])];
        while (padded.length < FIELD_SLOTS) {
            padded.push('');
        }
        return padded.slice(0, FIELD_SLOTS);
    }

    function normalizeShopToPerpage(shop) {
        return shop === '1' || shop === 1 || shop === true ? 1 : 2;
    }

    function parseStatementResponse(data) {
        const statements = {};
        const langs = [];
        Object.keys(data || {}).forEach((key) => {
            const match = /^statement([A-Z][a-z]*)$/.exec(key);
            if (!match) return;
            const lang = match[1];
            langs.push(lang);
            const config = data[key] || {};
            statements[lang] = {
                fields: padFields(config.fields),
                contents: Array.isArray(config.contents) ? [...config.contents] : [''],
            };
        });
        // Keep Pl first, then the rest in API order
        langs.sort((a, b) => {
            if (a === 'Pl') return -1;
            if (b === 'Pl') return 1;
            return 0;
        });
        return { statements, langs };
    }

    export default {
        name: 'PanelCompetitionSettingStatement',
        components: {
            PanelCompetitionFormBox,
            PanelInputBaseRadio,
            PanelInputArea,
        },
        props: {
            id: {
                type: Number,
                required: true,
            },
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                saving: false,
                loadingDefault: false,
                loadingPdfBulk: false,
                loadingPdfEmpty: false,
                activeLang: 'Pl',
                langs: [],
                statements: {},
                fieldOptions: [],
                order: 'lastname',
                perpage: 1,
                orderOptions: [
                    { value: 'lastname', title: 'Alfabetycznie' },
                    { value: 'playerNumber', title: 'Zgodnie z numeracją' },
                ],
            };
        },
        computed: {
            loadingPdf() {
                return this.loadingPdfBulk || this.loadingPdfEmpty;
            },
            activeStatement() {
                return (
                    this.statements[this.activeLang] || {
                        fields: padFields([]),
                        contents: [''],
                    }
                );
            },
        },
        created() {
            this.load();
        },
        methods: {
            async load() {
                try {
                    const [statementData, fieldsData] = await Promise.all([
                        axios.get(`panel/competitions/${this.id}/statement`),
                        axios.get(`panel/competitions/${this.id}/statement/fields`),
                    ]);

                    const { statements, langs } = parseStatementResponse(statementData);
                    this.statements = statements;
                    this.langs = langs.length ? langs : ['Pl'];
                    this.activeLang = this.langs.includes('Pl') ? 'Pl' : this.langs[0];

                    if (statementData.statementPl) {
                        this.perpage = normalizeShopToPerpage(statementData.statementPl.shop);
                    }

                    const standard = fieldsData?.standard || [];
                    const custom = fieldsData?.custom || [];
                    this.fieldOptions = [...standard, ...custom];
                    if (!this.fieldOptions.some((o) => o.value === '')) {
                        this.fieldOptions.unshift({ value: '', label: 'Brak' });
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('load statement error:', error);
                } finally {
                    this.ready = true;
                }
            },
            setField(index, value) {
                const statement = this.statements[this.activeLang];
                if (!statement) return;
                this.$set(statement.fields, index, value == null ? '' : value);
            },
            setContent(index, value) {
                const statement = this.statements[this.activeLang];
                if (!statement) return;
                this.$set(statement.contents, index, value);
            },
            addContent() {
                const statement = this.statements[this.activeLang];
                if (!statement) return;
                statement.contents.push('');
            },
            removeContent(index) {
                const statement = this.statements[this.activeLang];
                if (!statement) return;
                statement.contents.splice(index, 1);
            },
            contentRows(content) {
                if (!content) return 6;
                return content.length > 400 ? 14 : 6;
            },
            buildSavePayload() {
                const payload = {};
                this.langs.forEach((lang) => {
                    const statement = this.statements[lang];
                    if (!statement) return;
                    payload[`statement${lang}`] = {
                        fields: [...statement.fields],
                        contents: [...statement.contents],
                    };
                });
                return payload;
            },
            async save() {
                this.saving = true;
                try {
                    const response = await axios.put(
                        `panel/competitions/${this.id}/statement`,
                        this.buildSavePayload()
                    );
                    const { statements, langs } = parseStatementResponse(response);
                    this.statements = statements;
                    this.langs = langs.length ? langs : this.langs;
                    if (!this.langs.includes(this.activeLang)) {
                        this.activeLang = this.langs.includes('Pl') ? 'Pl' : this.langs[0];
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('save statement error:', error);
                } finally {
                    this.saving = false;
                }
            },
            async restoreDefault() {
                this.loadingDefault = true;
                try {
                    const response = await axios.post(`panel/competitions/${this.id}/statement/default`);
                    if (response.statementPl) {
                        this.$set(this.statements, 'Pl', {
                            fields: padFields(response.statementPl.fields),
                            contents: Array.isArray(response.statementPl.contents)
                                ? [...response.statementPl.contents]
                                : [''],
                        });
                        if (!this.langs.includes('Pl')) {
                            this.langs = ['Pl', ...this.langs];
                        }
                        this.activeLang = 'Pl';
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('restore default statement error:', error);
                } finally {
                    this.loadingDefault = false;
                }
            },
            async downloadBulkPdf() {
                this.loadingPdfBulk = true;
                try {
                    const data = await axios.get(`panel/statements/competition/${this.id}`, {
                        params: {
                            order: this.order,
                            perpage: this.perpage,
                        },
                        responseType: 'blob',
                    });
                    this.triggerBlobDownload(data, `statements-${this.competition.name || this.id}.pdf`);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('download bulk statement pdf error:', error);
                } finally {
                    this.loadingPdfBulk = false;
                }
            },
            async downloadEmptyPdf() {
                this.loadingPdfEmpty = true;
                try {
                    const data = await axios.get(`panel/statements/competition/${this.id}/empty`, {
                        params: {
                            perpage: this.perpage,
                        },
                        responseType: 'blob',
                    });
                    this.triggerBlobDownload(data, `statements-empty-${this.competition.name || this.id}.pdf`);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('download empty statement pdf error:', error);
                } finally {
                    this.loadingPdfEmpty = false;
                }
            },
            triggerBlobDownload(data, filename) {
                const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            },
        },
    };
</script>

<style scoped>
    .competition-statement {
        width: 100%;
    }

    .radio-wrap-column {
        display: flex;
        flex-direction: column;
    }

    .form-hint {
        line-height: 18px;
    }

    .optional {
        font-weight: 400;
        opacity: 0.85;
    }

    .radios2 input[type='radio'] {
        display: none;
    }

    .radios2 input[type='radio'] + label {
        padding-left: 32px;
        display: inline-block;
        cursor: pointer;
        position: relative;
        line-height: 32px;
        user-select: none;
    }

    .radios2 input[type='radio'] + label:before {
        content: '';
        box-sizing: border-box;
        transition: all 0.1s ease-in-out;
        display: inline-block;
        width: 20px;
        height: 20px;
        margin: 6px;
        position: absolute;
        left: 0;
        top: 1px;
        z-index: 2;
        border: 1px solid #fff;
        border-radius: 20px;
        background-color: transparent;
    }

    .radios2 input[type='radio']:checked + label:before {
        background-color: #ff3d00;
        border-width: 5px;
    }

    .statement-preview .txt--s1 {
        font-size: 12px;
        color: var(--color-blue-grey, #78909c);
        margin-bottom: 4px;
    }

    .statement-preview .txt--s2 {
        font-size: 14px;
        margin-bottom: 0;
    }

    .pull-right {
        float: right;
    }

    .lang-switcher {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .lang-switcher__btn {
        min-width: 48px;
        padding: 8px 14px;
        border: 1px solid var(--color-blue-grey, #78909c);
        border-radius: 4px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-weight: 600;
    }

    .lang-switcher__btn--active {
        background: var(--color-dark-pastel-green, #66bb6a);
        border-color: var(--color-dark-pastel-green, #66bb6a);
        color: #fff;
    }

    .competition-statement >>> .input-field {
        height: 40px;
        min-height: auto;
    }

    .add-new-content-wrap {
        border: 1px solid var(--color-bluish-grey, #cfd8dc);
        padding: 16px 32px;
    }

    .statement-field-select {
        width: 95%;
    }

    @media (min-width: 992px) {
        .form-hint-wrap {
            margin-left: 16px;
        }
        .ml-md-30 {
            margin-left: 30px;
        }
        .add-new-content-wrap {
            display: flex;
            justify-content: flex-end;
        }
        .add-new-content-wrap >>> .btn {
            min-width: 200px;
        }
    }
</style>
